import { sortBy } from 'ramda'
import { CourseProgressSummary } from '../types'

export type ProgressTagColor =
  'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'

export type ProgressCtaKind = 'resume' | 'quiz' | 'survey' | 'certificate'

// Button's color prop has no 'default' — narrower than the chip's tag color,
// since a CTA button only ever renders for the non-default rows below.
export type ProgressCtaColor = Exclude<ProgressTagColor, 'default'>

let stepWord = (count: number) => (count === 1 ? 'step' : 'steps')

export type ProgressDisplay = {
  tagLabel: string
  tagColor: ProgressTagColor
  subtitle: string
  cta: {
    label: string
    variant: 'contained' | 'outlined'
    color: ProgressCtaColor
    kind: ProgressCtaKind
  } | null
  // Lower sorts first. Ranks the row's urgency for the "Continue & action
  // items" surfaces — a stalled/blocking signal (a failed quiz, a quiz or
  // survey standing between the learner and a certificate) outranks plain
  // "in progress", which is the default, always-available, least-urgent row.
  priority: number
}

// Pure display mapping for a CourseProgressSummary, evaluated top-to-bottom —
// first match wins. An issued certificate on a completed course is checked
// first and outranks everything, including nextStep — it's the one signal
// that should never be second-guessed by a possibly-stale quiz/survey/nextStep
// value. Below that, order mirrors the priority already implicit in the type:
// a scored nextStep (certificate/survey/retake) outranks a live quiz or step
// count, since it's the most specific signal the backend can send for a course
// that isn't already done.
//
// nextStep.retakeQuiz carries score/passingScore as correct-answer counts,
// not percentages — see the retakeQuiz branch below for why no ratio is
// displayed (this endpoint has no total question count to divide by).
//
// Total and side-effect free: every reachable CourseProgressSummary produces
// a row, including the notStarted case, which is unreachable given
// isActionableProgress but kept here so this function never throws.
export let progressDisplayFor = (
  progress: CourseProgressSummary,
): ProgressDisplay => {
  let { totalSteps, completedSteps, state, certificate, version } = progress

  // Defense in depth: quiz/nextStep are only ever meaningful for a v2-published course — v1 has
  // no per-course quiz session model this endpoint can see (see CourseProgressSummary's own
  // comment). Backend already enforces that (buildV1Summary hardcodes both to null), but a
  // version === 1 course whose quiz/nextStep arrive non-null anyway — from any cause, known or
  // not — should still never be read here. This unconditional guard, not the backend's, is what
  // actually makes that true regardless of what upstream classification produced.
  let quiz = version === 2 ? progress.quiz : null
  let nextStep = version === 2 ? progress.nextStep : null

  if (state === 'completed' && certificate === 'issued') {
    // Checked before every quiz/survey/nextStep branch below, deliberately: an issued
    // certificate is the most authoritative signal this endpoint can send, and nothing else
    // should be able to override it back into "action needed." This matters for a course
    // migrated from v1 to v2 after a learner already completed and was certified for it under
    // v1 — v1 quizzes have no session history to carry into v2's QuizSession model, so `quiz`
    // can read `notAttempted` (or, before the backend fix, used to) for a course this learner
    // finished long ago. Backend now nulls `quiz`/`nextStep` for exactly that case, but this
    // check stays regardless — a completed, certificated course should never show "quiz due"
    // no matter what produces a stale secondary signal.
    return {
      tagLabel: 'Completed',
      tagColor: 'success',
      subtitle: 'Course complete',
      cta: null,
      priority: 5,
    }
  }

  if (nextStep?.kind === 'claimCertificate') {
    // Backend only sets this nextStep once nothing is blocking — quiz passed
    // AND (survey submitted OR not required) — so this is genuinely the
    // all-clear state. The subtitle differentiates by which of those two
    // survey states got them here; neither implies anything is still owed.
    return {
      tagLabel: 'Certificate ready',
      tagColor: 'success',
      subtitle:
        progress.survey?.kind === 'submitted'
          ? 'Quiz and survey complete — certificate ready'
          : 'All steps complete — certificate ready',
      cta: {
        label: 'Claim certificate',
        variant: 'contained',
        color: 'success',
        kind: 'certificate',
      },
      priority: 3,
    }
  }

  if (nextStep?.kind === 'submitSurvey') {
    return {
      tagLabel: 'Survey needed',
      tagColor: 'warning',
      subtitle: 'Quiz passed — survey required to finish',
      cta: {
        label: 'Submit survey',
        variant: 'contained',
        color: 'warning',
        kind: 'survey',
      },
      priority: 2,
    }
  }

  if (nextStep?.kind === 'retakeQuiz') {
    // score/passingScore are correct-answer COUNTS, not percentages (see
    // classroom-v2/components/quiz-result-step.tsx and pre-quiz-step.tsx,
    // which use the same "X of Y correct" framing) — this endpoint doesn't
    // carry the total question count, so a fabricated ratio/percentage
    // isn't possible here without it.
    return {
      tagLabel: 'Retake quiz',
      tagColor: 'error',
      subtitle: `Scored ${nextStep.score} correct — needed ${nextStep.passingScore} to pass`,
      cta: {
        label: 'Retake quiz',
        variant: 'outlined',
        color: 'error',
        kind: 'quiz',
      },
      priority: 0,
    }
  }

  if (
    quiz?.kind === 'inProgress' ||
    (quiz?.kind === 'notAttempted' && completedSteps === totalSteps)
  ) {
    // quiz.kind === 'inProgress' means a session was opened, which doesn't
    // require every step to be finished first — so the "all steps complete"
    // subtitle only applies when that's actually true; otherwise report the
    // real step count alongside the in-progress attempt.
    return {
      tagLabel: 'Quiz due',
      tagColor: 'warning',
      subtitle:
        completedSteps === totalSteps
          ? `All ${totalSteps} ${stepWord(totalSteps)} complete — quiz remaining`
          : `${completedSteps} of ${totalSteps} ${stepWord(totalSteps)} complete — quiz in progress`,
      cta: {
        label: 'Take the quiz',
        variant: 'contained',
        color: 'warning',
        kind: 'quiz',
      },
      priority: 1,
    }
  }

  if (state === 'completed' && certificate !== 'issued') {
    return {
      tagLabel: 'Certificate ready',
      tagColor: 'success',
      subtitle: 'Certificate is being generated',
      cta: {
        label: 'View status',
        variant: 'outlined',
        color: 'success',
        kind: 'certificate',
      },
      priority: 4,
    }
  }

  if (state === 'inProgress') {
    return {
      tagLabel: 'In progress',
      tagColor: 'primary',
      subtitle: `${completedSteps} of ${totalSteps} ${stepWord(totalSteps)} complete`,
      cta: {
        label: 'Resume',
        variant: 'contained',
        color: 'primary',
        kind: 'resume',
      },
      priority: 6,
    }
  }

  return {
    tagLabel: 'Not started',
    tagColor: 'default',
    subtitle: `0 of ${totalSteps} ${stepWord(totalSteps)} complete`,
    cta: null,
    priority: 7,
  }
}

// The "Continue & action items" filter: show a course only if there's
// something left for the LEARNER to do — resume content, take/retake a quiz,
// or submit a survey. Certificate rows (kind: 'certificate') are deliberately
// excluded even though progressDisplayFor gives them a CTA: by the time
// nextStep is claimCertificate or the course is completed-but-not-issued,
// the learner is done — claiming/waiting on the certificate is a backend
// formality, not an action item to "pick up where you left off" on. Built off
// progressDisplayFor itself rather than a second, hand-duplicated set of
// conditions, so the filter and the display can't drift apart.
export let isActionableProgress = (progress: CourseProgressSummary) => {
  let { cta } = progressDisplayFor(progress)
  return cta !== null && cta.kind !== 'certificate'
}

// Most urgent first, "in progress" last — shared by the "Continue & action
// items" slider and the Continue Watching page so the two never rank
// differently. Stable sort: ties (e.g. two "Quiz due" rows) keep the
// courseID order the API already guarantees.
export let sortByUrgency = (
  progress: CourseProgressSummary[],
): CourseProgressSummary[] =>
  sortBy((p: CourseProgressSummary) => progressDisplayFor(p).priority, progress)

export let ctaRouteFor = (
  progress: CourseProgressSummary,
  kind: ProgressCtaKind,
) =>
  progress.version === 1 || kind === 'resume'
    ? `/self-paced/course/${progress.courseID}`
    : `/classroom-v2/${progress.courseID}/player`
